import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function WhoItsForPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: "Who Automigo Is For — Local Service Businesses on Long Island",
    description: 'Automigo is built for local service businesses that receive customer inquiries and need to respond quickly to win jobs.',
    url: `${siteUrl}/who-its-for`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: "Who It's For", item: `${siteUrl}/who-its-for` }
      ]
    },
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What types of businesses benefit from Automigo?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Contractors, HVAC, plumbing, electrical, handyman, cleaning services, power washing, lawn care, property maintenance, repair technicians, installers, and local specialists.'
          }
        },
        {
          '@type': 'Question',
          name: 'Is Automigo right for my business?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Automigo works best if you miss messages while working, customers contact multiple providers, replies sometimes happen hours later, or leads go cold before you respond.'
          }
        },
        {
          '@type': 'Question',
          name: 'When is Automigo probably not necessary?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Automigo is probably not necessary if customers book instantly online, you don\'t rely on inbound requests, or every inquiry already gets an immediate reply.'
          }
        }
      ]
    }
  };

  const recognitionPoints = [
    'You miss messages while working',
    'Customers contact multiple providers',
    'Replies sometimes happen hours later',
    'You handle conversations from your phone',
    'Leads go cold before you respond'
  ];

  const businessCategories = [
    {
      title: 'Home & Field Services',
      businesses: ['Contractors', 'HVAC', 'Plumbing', 'Electrical', 'Handyman']
    },
    {
      title: 'Cleaning & Maintenance',
      businesses: ['Cleaning services', 'Power washing', 'Lawn care', 'Property maintenance']
    },
    {
      title: 'Appointment-Based Services',
      businesses: ['Repair technicians', 'Installers', 'Local specialists']
    }
  ];

  const notFitPoints = [
    'Customers book instantly online',
    'You don\'t rely on inbound requests',
    'Every inquiry already gets an immediate reply'
  ];

  return (
    <>
      <SEOHead
        title="Who It's For | Automigo for Local Service Businesses on Long Island"
        description="Automigo is built for local service businesses that receive customer inquiries and need to respond quickly to win jobs. See if automated lead response is right for your business."
        keywords="lead automation for contractors, SMS automation for businesses, home service business automation, local business lead management, service business marketing Long Island"
        canonical={`${siteUrl}/who-its-for`}
        schema={schema}
      />

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center justify-between h-20">
              <div>
                <Link to="/" className="flex items-center gap-3 cursor-pointer">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                    <i className="ri-robot-2-line text-white text-xl"></i>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">Automigo</span>
                </Link>
                <p className="text-xs text-gray-500 mt-1 ml-1">Serving Long Island Businesses</p>
              </div>
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">Home</Link>
                <Link to="/how-it-works" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">How It Works</Link>
                <Link to="/who-its-for" className="text-sm font-medium text-teal-600 cursor-pointer">Who It's For</Link>
                <Link to="/about-us" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">About</Link>
                <Link to="/#contact" className="bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">Get Started</Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Who Automigo Is For
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Automigo works best for businesses that receive customer inquiries and need to respond quickly to win the job.
            </p>
          </div>
        </section>

        {/* Recognition Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              You'll Benefit Most If…
            </h2>

            <div className="space-y-4">
              {recognitionPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-4 bg-white border border-gray-200 p-6 rounded-xl">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                  </div>
                  <p className="text-gray-700 text-lg">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Business Types Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Typical Businesses Using Automigo
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {businessCategories.map((category, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 pb-4 border-b-2 border-teal-600">
                    {category.title}
                  </h3>
                  <ul className="space-y-3">
                    {category.businesses.map((business, i) => (
                      <li key={i} className="text-gray-700 flex items-center gap-2">
                        <span className="text-teal-600">•</span>
                        {business}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Not A Fit Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Probably Not Necessary If…
            </h2>

            <div className="space-y-4">
              {notFitPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-4 bg-gray-50 border border-gray-200 p-6 rounded-xl">
                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-close-line text-gray-500 text-sm"></i>
                  </div>
                  <p className="text-gray-600 text-lg">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reassurance Section */}
        <section className="py-20 bg-teal-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <p className="text-2xl md:text-3xl font-medium text-gray-900">
              Automigo doesn't replace how you communicate —<br />
              it makes sure conversations don't get missed.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-500">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              See how it would work for your business
            </h2>
            <Link 
              to="/#contact"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get My Free Automation Plan
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-8">
              <p className="text-gray-400 text-sm">Serving businesses across Long Island, NY</p>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-robot-2-line text-white text-xl"></i>
                </div>
                <span className="text-2xl font-bold text-white">Automigo</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6">
                <Link to="/about-us" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">About</Link>
                <Link to="/how-it-works" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">How It Works</Link>
                <Link to="/#contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Contact</Link>
                <Link to="/terms-and-conditions" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Messaging Terms</Link>
                <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Privacy</Link>
              </div>
              <div className="flex items-center gap-6">
                <p className="text-sm text-gray-500">© 2025 Automigo</p>
                <a href="https://readdy.ai/?ref=logo" className="text-sm text-gray-500 hover:text-white transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
